import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  AddDeliveryRegionOptions,
  AddDeliveryRegionResponse,
  CreateShippingOptionApplicationErrors,
  QueryShippingOptionsResponse,
  RemoveDeliveryRegionOptions,
  RemoveDeliveryRegionResponse,
  ShippingOption,
  ShippingOptionCreatedEnvelope,
  ShippingOptionDeletedEnvelope,
  ShippingOptionQuery,
  ShippingOptionUpdatedEnvelope,
  ShippingOptionsQueryBuilder,
  UpdateExtendedFieldsOptions,
  UpdateExtendedFieldsResponse,
  UpdateShippingOption,
  UpdateShippingOptionApplicationErrors,
  addDeliveryRegion as universalAddDeliveryRegion,
  createShippingOption as universalCreateShippingOption,
  deleteShippingOption as universalDeleteShippingOption,
  getShippingOption as universalGetShippingOption,
  queryShippingOptions as universalQueryShippingOptions,
  removeDeliveryRegion as universalRemoveDeliveryRegion,
  typedQueryShippingOptions as universalTypedQueryShippingOptions,
  updateExtendedFields as universalUpdateExtendedFields,
  updateShippingOption as universalUpdateShippingOption,
} from './ecom-v1-shipping-option-shipping-options.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/ecom' };

export function createShippingOption(
  httpClient: HttpClient
): CreateShippingOptionSignature {
  return (shippingOption: ShippingOption) =>
    universalCreateShippingOption(
      shippingOption,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateShippingOptionSignature {
  /**
   * Creates a ShippingOption.
   *
   * The request body must include shipping option and delivery region associated to.
   * @param - ShippingOption to be created.
   * @returns The created ShippingOption.
   */
  (shippingOption: ShippingOption): Promise<
    NonNullablePaths<
      ShippingOption,
      | `deliveryRegionId`
      | `deliveryRegionIds`
      | `rates`
      | `rates.${number}.amount`
      | `rates.${number}.multiplyByQuantity`,
      4
    > & {
      __applicationErrorsType?: CreateShippingOptionApplicationErrors;
    }
  >;
}

export function getShippingOption(
  httpClient: HttpClient
): GetShippingOptionSignature {
  return (shippingOptionId: string) =>
    universalGetShippingOption(
      shippingOptionId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetShippingOptionSignature {
  /**
   * Retrieves a ShippingOption.
   * @param - ID of the ShippingOption to retrieve.
   * @returns The requested ShippingOption.
   */
  (shippingOptionId: string): Promise<
    NonNullablePaths<
      ShippingOption,
      | `deliveryRegionId`
      | `deliveryRegionIds`
      | `rates`
      | `rates.${number}.amount`
      | `rates.${number}.multiplyByQuantity`,
      4
    >
  >;
}

export function updateShippingOption(
  httpClient: HttpClient
): UpdateShippingOptionSignature {
  return (
    _id: string,
    shippingOption: NonNullablePaths<UpdateShippingOption, `revision`, 2>
  ) =>
    universalUpdateShippingOption(
      _id,
      shippingOption,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateShippingOptionSignature {
  /**
   * Updates a ShippingOption.
   *
   *
   * Each time the ShippingOption is updated,
   * `revision` increments by 1.
   * The current `revision` must be passed when updating the ShippingOption.
   * This ensures you're working with the latest ShippingOption
   * and prevents unintended overwrites.
   * @param - ShippingOption ID.
   * @returns Updated ShippingOption.
   */
  (
    _id: string,
    shippingOption: NonNullablePaths<UpdateShippingOption, `revision`, 2>
  ): Promise<
    NonNullablePaths<
      ShippingOption,
      | `deliveryRegionId`
      | `deliveryRegionIds`
      | `rates`
      | `rates.${number}.amount`
      | `rates.${number}.multiplyByQuantity`,
      4
    > & {
      __applicationErrorsType?: UpdateShippingOptionApplicationErrors;
    }
  >;
}

export function deleteShippingOption(
  httpClient: HttpClient
): DeleteShippingOptionSignature {
  return (shippingOptionId: string) =>
    universalDeleteShippingOption(
      shippingOptionId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteShippingOptionSignature {
  /**
   * Deletes a ShippingOption.
   * Deleting a ShippingOption permanently removes them from the ShippingOption List.
   * @param - Id of the ShippingOption to delete.
   */
  (shippingOptionId: string): Promise<void>;
}

export function queryShippingOptions(
  httpClient: HttpClient
): QueryShippingOptionsSignature {
  return () =>
    universalQueryShippingOptions(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryShippingOptionsSignature {
  /**
   * Retrieves a list of ShippingOptions, given the provided [paging, filtering, and sorting][1].
   *
   * Up to 1,000 ShippingOptions can be returned per request.
   *
   * To learn how to query ShippingOptions, see [API Query Language][2].
   *
   * [1]: https://dev.wix.com/api/rest/getting-started/sorting-and-paging
   * [2]: https://dev.wix.com/api/rest/getting-started/api-query-language
   */
  (): ShippingOptionsQueryBuilder;
}

export function typedQueryShippingOptions(
  httpClient: HttpClient
): TypedQueryShippingOptionsSignature {
  return (query: ShippingOptionQuery) =>
    universalTypedQueryShippingOptions(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryShippingOptionsSignature {
  /** */
  (query: ShippingOptionQuery): Promise<
    NonNullablePaths<
      QueryShippingOptionsResponse,
      `shippingOptions` | `shippingOptions.${number}.deliveryRegionId`,
      4
    >
  >;
}

export function updateExtendedFields(
  httpClient: HttpClient
): UpdateExtendedFieldsSignature {
  return (
    _id: string,
    namespace: string,
    options: NonNullablePaths<UpdateExtendedFieldsOptions, `namespaceData`, 2>
  ) =>
    universalUpdateExtendedFields(
      _id,
      namespace,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateExtendedFieldsSignature {
  /**
   * Updates extended fields of a ShippingOption without incrementing revision
   * @param - ID of the entity to update.
   * @param - Identifier for the app whose extended fields are being updated.
   */
  (
    _id: string,
    namespace: string,
    options: NonNullablePaths<UpdateExtendedFieldsOptions, `namespaceData`, 2>
  ): Promise<
    NonNullablePaths<
      UpdateExtendedFieldsResponse,
      | `shippingOption.deliveryRegionId`
      | `shippingOption.deliveryRegionIds`
      | `shippingOption.rates`
      | `shippingOption.rates.${number}.amount`
      | `shippingOption.rates.${number}.multiplyByQuantity`,
      5
    >
  >;
}

export function addDeliveryRegion(
  httpClient: HttpClient
): AddDeliveryRegionSignature {
  return (
    shippingOptionId: string,
    deliveryRegionId: string,
    options: NonNullablePaths<AddDeliveryRegionOptions, `revision`, 2>
  ) =>
    universalAddDeliveryRegion(
      shippingOptionId,
      deliveryRegionId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface AddDeliveryRegionSignature {
  /**
   * Adds a delivery region to a shipping option.
   * @param - ID of the ShippingOption to be updated.
   * @param - ID of the delivery region that should use this ShippingOption.
   */
  (
    shippingOptionId: string,
    deliveryRegionId: string,
    options: NonNullablePaths<AddDeliveryRegionOptions, `revision`, 2>
  ): Promise<
    NonNullablePaths<
      AddDeliveryRegionResponse,
      | `shippingOption.deliveryRegionId`
      | `shippingOption.deliveryRegionIds`
      | `shippingOption.rates`
      | `shippingOption.rates.${number}.amount`
      | `shippingOption.rates.${number}.multiplyByQuantity`,
      5
    >
  >;
}

export function removeDeliveryRegion(
  httpClient: HttpClient
): RemoveDeliveryRegionSignature {
  return (
    shippingOptionId: string,
    deliveryRegionId: string,
    options: NonNullablePaths<RemoveDeliveryRegionOptions, `revision`, 2>
  ) =>
    universalRemoveDeliveryRegion(
      shippingOptionId,
      deliveryRegionId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface RemoveDeliveryRegionSignature {
  /**
   * Removes a delivery region from a shipping option.
   * @param - ID of the ShippingOption to be updated.
   * @param - ID of the delivery region that should not use this ShippingOption anymore.
   */
  (
    shippingOptionId: string,
    deliveryRegionId: string,
    options: NonNullablePaths<RemoveDeliveryRegionOptions, `revision`, 2>
  ): Promise<
    NonNullablePaths<
      RemoveDeliveryRegionResponse,
      | `shippingOption.deliveryRegionId`
      | `shippingOption.deliveryRegionIds`
      | `shippingOption.rates`
      | `shippingOption.rates.${number}.amount`
      | `shippingOption.rates.${number}.multiplyByQuantity`,
      5
    >
  >;
}

export const onShippingOptionCreated = /*#__PURE__*/ EventDefinition(
  'wix.ecom.v1.shipping_option_created',
  true,
  (event: ShippingOptionCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<ShippingOptionCreatedEnvelope>();
export const onShippingOptionDeleted = /*#__PURE__*/ EventDefinition(
  'wix.ecom.v1.shipping_option_deleted',
  true,
  (event: ShippingOptionDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<ShippingOptionDeletedEnvelope>();
export const onShippingOptionUpdated = /*#__PURE__*/ EventDefinition(
  'wix.ecom.v1.shipping_option_updated',
  true,
  (event: ShippingOptionUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
            { path: 'modifiedFields.createdDate' },
            { path: 'modifiedFields.updatedDate' },
          ],
        },
      ])
    )
)<ShippingOptionUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  AddDeliveryRegionOptions,
  AddDeliveryRegionRequest,
  AddDeliveryRegionResponse,
  BaseEventMetadata,
  Condition,
  ConditionType,
  ConditionalRates,
  CreateShippingOptionRequest,
  CreateShippingOptionResponse,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  DeleteShippingOptionRequest,
  DeleteShippingOptionResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  ExtendedFields,
  GetShippingOptionRequest,
  GetShippingOptionResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  LogicalOperator,
  MessageEnvelope,
  QueryShippingOptionsRequest,
  QueryShippingOptionsResponse,
  RemoveDeliveryRegionOptions,
  RemoveDeliveryRegionRequest,
  RemoveDeliveryRegionResponse,
  RestoreInfo,
  ShippingOption,
  ShippingOptionCreatedEnvelope,
  ShippingOptionDeletedEnvelope,
  ShippingOptionQuerySpec,
  ShippingOptionUpdatedEnvelope,
  ShippingOptionsQueryBuilder,
  ShippingOptionsQueryResult,
  SortOrder,
  Sorting,
  UpdateExtendedFieldsOptions,
  UpdateExtendedFieldsRequest,
  UpdateExtendedFieldsResponse,
  UpdateShippingOption,
  UpdateShippingOptionRequest,
  UpdateShippingOptionResponse,
  WebhookIdentityType,
  utils,
} from './ecom-v1-shipping-option-shipping-options.universal.js';
